/**
 * SignIn — split-pane auth page. Left column: copy + a single tilted
 * sample FloatingCard. Right column: white "Sign in" card stacking
 * Apple/Google buttons + email/password fallback.
 *
 * Renders inside PublicShell so the sticky top nav + footer come
 * for free. The card is intentionally short on chrome — the page
 * itself is the sales pitch via the left column.
 */
import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, sessionStore } from '@/lib/api';
import { Btn, Eyebrow, Field, FloatingCard, Icons } from '@/components';
import { OAuthButtons } from '@/components/OAuthButtons';
import { friendlyError } from '@/lib/errors';

export function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const pair = await api.signin({ email, password });
      sessionStore.set(pair.access, pair.refresh, pair.user);
      navigate(pair.user.profile_complete ? '/home' : '/builder');
    } catch (err) {
      setError(friendlyError(err, 'SignIn'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="auth-split"
      style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '60px 40px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 80,
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)' }}
    >
      {/* LEFT — copy + sample card */}
      <div>
        <Eyebrow tone="coral" letter={3} style={{ marginBottom: 18 }}>Welcome back</Eyebrow>
        <h1
          className="serif auth-headline"
          style={{
            fontSize: 64,
            fontWeight: 500,
            letterSpacing: -1.8,
            lineHeight: 1.02,
            margin: 0,
            textWrap: 'balance' as const }}
        >
          Sign back <span style={{ fontStyle: 'italic' }}>in.</span>
        </h1>
        <p style={{ fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.5, marginTop: 20, maxWidth: 460 }}>
          Your ratings, your notes, your card — exactly where you left them.
        </p>

        {/* Sample card peek */}
        <div className="auth-sample-card" style={{ marginTop: 36, position: 'relative', width: 280, height: 220 }}>
          <FloatingCard rotate={-4} top={0} left={0} z={1} variant={1} initial="M" name="You" score={7.8} primary />
        </div>
      </div>

      {/* RIGHT — auth card */}
      <div style={{ maxWidth: 440, justifySelf: 'center', width: '100%' }}>
        <div className="auth-card" style={{ background: 'var(--surface)', borderRadius: 24, padding: 32, boxShadow: 'var(--shadow-lift)' }}>
          <div className="serif" style={{ fontSize: 28, fontWeight: 500, letterSpacing: -0.5 }}>Sign in</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>with the account you used before</div>

          {!showEmail ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
              <OAuthButtons mode="signin" />
              <Divider />
              <Btn
                variant="outline"
                size="lg"
                full
                icon={<Icons.user size={16} />}
                onClick={() => { setShowEmail(true); setError(null); }}
              >
                Sign in with email
              </Btn>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24 }}>
              <Field label="Email">
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={inputStyle}
                />
              </Field>
              <Field label="Password">
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
              </Field>
              <div style={{ textAlign: 'right', marginTop: -8 }}>
                <Link to="/auth/forgot" style={{ color: 'var(--ink-3)', fontSize: 12 }}>Forgot password?</Link>
              </div>
              {error && (
                <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
                  {error}
                </div>
              )}
              <Btn variant="primary" size="lg" full type="submit" loading={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Btn>
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setShowEmail(false); setError(null); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--coral)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                >
                  ← Sign in with Apple or Google
                </button>
              </div>
            </form>
          )}

          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink-3)', marginTop: 22, lineHeight: 1.5 }}>
            Don't have an account?{' '}
            <Link to="/auth/signup" style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline' }}>
              Sign up
            </Link>
          </div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.5 }}>
          By continuing you agree to our <Link to="/terms" style={{ color: 'inherit' }}><u>Terms</u></Link> and{' '}
          <Link to="/privacy" style={{ color: 'inherit' }}><u>Privacy Policy</u></Link>.
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div
      className="mono"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: 'var(--ink-4)',
        fontSize: 10,
        letterSpacing: 2,
        margin: '8px 0' }}
    >
      <div style={{ flex: 1, height: 1, background: 'var(--line-strong)' }} />
      OR
      <div style={{ flex: 1, height: 1, background: 'var(--line-strong)' }} />
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg)',
  borderRadius: 12,
  padding: '14px 16px',
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  color: 'var(--ink)',
  border: '1.5px solid transparent',
  outline: 'none' };
